/**
 * The brightness a phone photo has on screen and nowhere else.
 *
 * ── What is actually wrong ──────────────────────────────────────────────────────────────
 * A photo taken on a modern Android phone is not one picture. It is a Google Ultra HDR file:
 * an ordinary JPEG, and, tacked on the end of the same file, a second small JPEG called a
 * gain map — a per-pixel record of how much brighter each part of the scene should be when
 * there is a display bright enough to show it. The phone applies that map when it draws the
 * picture. A 2-D canvas does not, and cannot: it is a standard-range surface, so it gets the
 * ordinary picture underneath and nothing else.
 *
 * Every output this studio makes goes through a canvas — crop, save, print, share. So on a
 * phone the sheet showed a bright photo and every single thing made from it came out darker,
 * and on a desktop, where the screen has no headroom to show the map either, nothing looked
 * wrong at all. Measured on the photo that prompted this: shadows identical, midtones down
 * about 25 levels, highlights down about 60, colour untouched. That shape — shadows fine,
 * highlights gone, white still white — is a missing highlight boost and nothing else.
 *
 * ── What this does about it ─────────────────────────────────────────────────────────────
 * Reads the gain map out of the file and applies it to the pixels, so what gets cropped,
 * saved, printed and shared carries the brightness the phone was showing.
 *
 * The file's own numbers say the map is worth up to 3.32 stops — ten times — in the
 * highlights. Nothing with ten times the range fits in an ordinary picture, so applying it
 * literally would turn every bright area to flat white. Two things prevent that:
 *
 *   • The map's constant part is dropped. A gain map is rarely zero anywhere; the photo
 *     measured here asks for one and a half stops even in its shadows. On the phone that
 *     part is absorbed as "the whole picture is displayed brighter", which is why the
 *     shadows look unchanged on screen. Subtracting a low percentile leaves only the part
 *     that VARIES across the picture — which is the part that actually reads as depth.
 *   • What is left is applied through a curve that cannot clip: out = xb/(1 + x(b-1)).
 *     It fixes black at black and white at white, it is the identity wherever the map asks
 *     for nothing, and it approaches white without ever reaching it, so a boosted highlight
 *     compresses into the space available instead of burning out.
 *
 * STRENGTH was chosen by measurement, not taste: at 2 the result lands within a few levels
 * of what the phone itself was displaying, band for band, across the whole tone range. At 3
 * the top three bands start to converge — that is the picture beginning to flatten — and at
 * 1 it is visibly short of the screen. One number, here, if it ever needs to move.
 */

// How much of the map's varying part to use. See the note above.
const STRENGTH = 2.0;
// …and a ceiling on the result, in stops, so a file with an unusually aggressive map cannot
// drive the curve so hard that everything piles up against white.
const MAX_STOPS = 2.5;
// The percentile treated as "the constant part". Not zero: a handful of unusually dark
// pixels should not decide what the whole picture counts as its floor.
const FLOOR_PERCENTILE = 0.10;

const XMP_TAG = 'http://ns.adobe.com/xap/1.0/';

const latin1 = new TextDecoder('latin1');

/** Pull one attribute out of an XMP blob. Returns null when it is not there. */
const attr = (xmp, name) => {
  const m = xmp.match(new RegExp(name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '="([^"]*)"'));
  return m ? m[1] : null;
};
/** …as a number, taking the first when the value is an RGB triple. */
const num = (xmp, name, dflt) => {
  const v = attr(xmp, name);
  if (v == null) return dflt;
  const f = parseFloat(String(v).trim().split(/\s+/)[0]);
  return Number.isFinite(f) ? f : dflt;
};

/**
 * Find the XMP packet inside a JPEG's APP1 segments.
 * Scanning the raw bytes rather than walking segments on purpose: the packet is small, it is
 * near the front, and a malformed segment length in somebody's camera output should not be
 * able to make this throw.
 */
function findXmp(bytes, from = 0, limit = 128 * 1024) {
  const end = Math.min(bytes.length, from + limit);
  const text = latin1.decode(bytes.subarray(from, end));
  const i = text.indexOf('<x:xmpmeta');
  if (i < 0) return null;
  const j = text.indexOf('</x:xmpmeta>', i);
  return j < 0 ? null : text.slice(i, j + 12);
}

/**
 * Does this JPEG carry a gain map, and where is it?
 *
 * The primary image's XMP holds a Container directory listing what else is in the file; the
 * gain map is the entry with Semantic="GainMap", and its Length is its size in bytes at the
 * very end of the file. That length is what locates it — but it is checked against the JPEG
 * start-of-image marker before being believed, and there is a fallback scan for the second
 * SOI in case a writer rounded the number.
 *
 * @returns {null | {gainStart:number, meta:object}}
 */
export function parseUltraHdr(bytes) {
  if (!bytes || bytes.length < 4 || bytes[0] !== 0xFF || bytes[1] !== 0xD8) return null;
  const head = latin1.decode(bytes.subarray(0, Math.min(bytes.length, 128 * 1024)));
  if (head.indexOf(XMP_TAG) < 0 || head.indexOf('GainMap') < 0) return null;

  const primaryXmp = findXmp(bytes);
  if (!primaryXmp || primaryXmp.indexOf('GainMap') < 0) return null;
  const lenMatch = primaryXmp.match(/Item:Semantic="GainMap"[^>]*Item:Length="(\d+)"/)
    || primaryXmp.match(/Item:Length="(\d+)"[^>]*Item:Semantic="GainMap"/);

  const isSoi = (p) => p > 2 && p < bytes.length - 1 && bytes[p] === 0xFF && bytes[p + 1] === 0xD8;
  let gainStart = -1;
  if (lenMatch) {
    const cand = bytes.length - parseInt(lenMatch[1], 10);
    if (isSoi(cand)) gainStart = cand;
  }
  if (gainStart < 0) {                       // fall back: the next start-of-image after the first
    for (let p = 2; p < bytes.length - 1; p++) if (isSoi(p)) { gainStart = p; break; }
  }
  if (gainStart < 0) return null;

  // The parameters live in the gain map's OWN xmp, not the primary's.
  const gmXmp = findXmp(bytes, gainStart) || '';
  if (String(attr(gmXmp, 'hdrgm:BaseRenditionIsHDR')).toLowerCase() === 'true') return null;

  const meta = {
    min:     num(gmXmp, 'hdrgm:GainMapMin', 0),
    max:     num(gmXmp, 'hdrgm:GainMapMax', 1),
    gamma:   num(gmXmp, 'hdrgm:Gamma', 1),
    offSDR:  num(gmXmp, 'hdrgm:OffsetSDR', 0),
    offHDR:  num(gmXmp, 'hdrgm:OffsetHDR', 0),
  };
  if (!(meta.max > meta.min)) return null;   // nothing to apply
  if (!(meta.gamma > 0)) meta.gamma = 1;
  return { gainStart, meta };
}

// ── Colour, done in the right space ───────────────────────────────────────────────────────
// The gain map is defined as a multiplier on LIGHT, not on the numbers a JPEG stores, so the
// pixels have to be linearised before it is applied and re-encoded afterwards. Skipping that
// step is what turns a brightness change into a colour change.
const SRGB_TO_LINEAR = new Float32Array(256);
for (let i = 0; i < 256; i++) {
  const c = i / 255;
  SRGB_TO_LINEAR[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}
const LINEAR_TO_SRGB = new Uint8ClampedArray(4096);
for (let i = 0; i < 4096; i++) {
  const l = i / 4095;
  LINEAR_TO_SRGB[i] = Math.round(255 * (l <= 0.0031308 ? 12.92 * l : 1.055 * Math.pow(l, 1 / 2.4) - 0.055));
}

/**
 * How much brighter each of the 256 possible gain values asks for. One small table, because
 * the gain map is a single grey byte per pixel and the answer depends on nothing else.
 */
function buildBoostLut(meta, floorStops) {
  const lut = new Float32Array(256);
  const { min, max, gamma } = meta;
  for (let g = 0; g < 256; g++) {
    const recovery = gamma === 1 ? g / 255 : Math.pow(g / 255, 1 / gamma);
    const stops = Math.min(MAX_STOPS, Math.max(0, (min + recovery * (max - min)) - floorStops) * STRENGTH);
    lut[g] = Math.pow(2, stops);
  }
  return lut;
}

/** The gain level below which a tenth of the picture sits — "the constant part". */
function floorStopsOf(gainBitmap, meta) {
  const N = 192;
  const c = document.createElement('canvas');
  c.width = N; c.height = N;
  const g = c.getContext('2d', { willReadFrequently: true });
  g.drawImage(gainBitmap, 0, 0, N, N);
  const d = g.getImageData(0, 0, N, N).data;
  const hist = new Uint32Array(256);
  for (let i = 0; i < N * N; i++) hist[d[i * 4]]++;
  let acc = 0;
  const want = N * N * FLOOR_PERCENTILE;
  let v = 0;
  for (; v < 256; v++) { acc += hist[v]; if (acc >= want) break; }
  const recovery = meta.gamma === 1 ? v / 255 : Math.pow(v / 255, 1 / meta.gamma);
  return meta.min + recovery * (meta.max - meta.min);
}

// One picture in, one canvas out, kept by url so a second crop does not pay for it again.
// Small, because these are full-resolution surfaces and a phone has no room for a gallery of
// them — and evicting only forgets the entry. It never frees the canvas by hand: a compositor
// loads every picture it needs before it draws any of them, so an entry pushed out mid-run is
// still being held, and still being drawn from, by the run that asked for it.
const cache = new Map();
const CACHE_MAX = 4;
const remember = (url, canvas) => {
  cache.set(url, canvas);
  while (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
  return canvas;
};

/**
 * Apply the gain map, if there is one.
 *
 * @param {string} url  the picture, as anything fetch can read (data:, blob:, https:)
 * @returns {Promise<HTMLCanvasElement|null>} null when the file is an ordinary picture, when
 *          it cannot be read, or when anything at all goes wrong — every caller falls back to
 *          loading it the way it always did, so a photo can never fail to appear because of
 *          this.
 */
export async function loadWithGainMap(url) {
  if (!url) return null;
  if (cache.has(url)) {
    const hit = cache.get(url);
    if (hit && hit.width) return hit;
    cache.delete(url);
  }
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const bytes = new Uint8Array(await res.arrayBuffer());
    const found = parseUltraHdr(bytes);
    if (!found) return null;

    const { gainStart, meta } = found;
    const [base, gain] = await Promise.all([
      createImageBitmap(new Blob([bytes.subarray(0, gainStart)], { type: 'image/jpeg' })),
      createImageBitmap(new Blob([bytes.subarray(gainStart)],   { type: 'image/jpeg' })),
    ]);

    const W = base.width, H = base.height;
    if (!W || !H) return null;
    const boostLut = buildBoostLut(meta, floorStopsOf(gain, meta));
    const { offSDR, offHDR } = meta;

    const out = document.createElement('canvas');
    out.width = W; out.height = H;
    const outCtx = out.getContext('2d');
    // A canvas stands in for an <img> everywhere this is used — drawImage takes either. But
    // the callers also ASK a picture how big it really is, and they ask an <img>'s question.
    // Without these the export paths read "no natural size" and quietly fall back to a
    // default density, which would trade the brightness fix for a resolution loss.
    Object.defineProperty(out, 'naturalWidth',  { value: W, configurable: true });
    Object.defineProperty(out, 'naturalHeight', { value: H, configurable: true });

    // ── A band at a time ────────────────────────────────────────────────────────────────
    // A twelve-megapixel photo is fifty megabytes as pixels, and this needs the picture, the
    // gain map at the same size, and the result. Three of those at once is where a phone
    // browser stops returning a canvas and starts returning nothing. Five hundred rows at a
    // time costs eight megabytes of working space and produces exactly the same answer.
    const BAND = 512;
    const strip = document.createElement('canvas');
    strip.width = W;
    const sctx = strip.getContext('2d', { willReadFrequently: true });
    const gstrip = document.createElement('canvas');
    gstrip.width = W;
    const gctx = gstrip.getContext('2d', { willReadFrequently: true });

    for (let y0 = 0; y0 < H; y0 += BAND) {
      const h = Math.min(BAND, H - y0);
      strip.height = h; gstrip.height = h;
      sctx.drawImage(base, 0, y0, W, h, 0, 0, W, h);
      // The gain map is usually a quarter of the size; the browser's own scaler does the
      // upsampling, which is both faster and smoother than anything worth writing here.
      const gy = (y0 / H) * gain.height, gh = (h / H) * gain.height;
      gctx.drawImage(gain, 0, gy, gain.width, gh, 0, 0, W, h);

      const img = sctx.getImageData(0, 0, W, h);
      const gd  = gctx.getImageData(0, 0, W, h).data;
      const d = img.data;
      for (let i = 0, n = W * h * 4; i < n; i += 4) {
        const boost = boostLut[gd[i]];          // the map is grey: one channel is the answer
        if (boost <= 1.0001) continue;          // the map asks for nothing here: leave it be
        const lr = SRGB_TO_LINEAR[d[i]], lg = SRGB_TO_LINEAR[d[i + 1]], lb = SRGB_TO_LINEAR[d[i + 2]];
        const Y = 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
        if (Y <= 1e-6) continue;
        // ── Brightness, not colour ────────────────────────────────────────────────────
        // The curve is driven by the pixel's LIGHT and the three channels are then scaled
        // together by what it asked for. Running it on each channel separately — which is
        // the obvious way, and what this did first — compresses the strong channel harder
        // than the weak one and pulls every colour towards grey: measured, a brightened
        // photo lost a seventh of its saturation that way. Scaling by one ratio moves the
        // pixel along its own colour, so the hue and the saturation come out untouched.
        const yb = Math.max(0, (Y + offSDR) * boost - offHDR);
        const beff = Math.max(1, yb / Y);
        let ratio = beff / (1 + Y * (beff - 1));   // = (Y·b/(1+Y(b-1))) / Y — cannot exceed 1/Y
        // …and never so far that a single channel runs past white, which would bend the
        // colour towards it. The brightest channel is what sets the ceiling.
        const mx = lr > lg ? (lr > lb ? lr : lb) : (lg > lb ? lg : lb);
        if (mx > 0 && ratio * mx > 1) ratio = 1 / mx;
        // The clamp is belt and braces: the ceiling above already keeps this in range, and an
        // index past the end of the table reads as undefined, which lands in the picture as a
        // black pixel — the one failure here that would be hard to trace back.
        const ir = lr * ratio * 4095, ig = lg * ratio * 4095, ib = lb * ratio * 4095;
        d[i]     = LINEAR_TO_SRGB[ir > 4095 ? 4095 : ir | 0];
        d[i + 1] = LINEAR_TO_SRGB[ig > 4095 ? 4095 : ig | 0];
        d[i + 2] = LINEAR_TO_SRGB[ib > 4095 ? 4095 : ib | 0];
      }
      outCtx.putImageData(img, 0, y0);
    }

    strip.width = 0; strip.height = 0;
    gstrip.width = 0; gstrip.height = 0;
    base.close?.(); gain.close?.();
    return remember(url, out);
  } catch {
    return null;                                // an ordinary picture, by any other route
  }
}

/** Forget everything — for when a picture's bytes have changed behind the same url. */
export function forgetGainMaps() {
  cache.clear();
}
